/**
 * RouteMatchPanel — G001 라우팅 매칭 데모
 * R12 #330: Gateway route matching demo panel
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { showcaseApi } from '../api/showcaseApi';
import type { RouteMatchResponse } from '../types/showcase';

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

export function RouteMatchPanel() {
  const { t } = useTranslation();
  const [method, setMethod] = useState('GET');
  const [path, setPath] = useState('/api/eraf/users');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<RouteMatchResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await showcaseApi.routeMatch({ method, path });
      setResult(res);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr?.message ?? 'Unknown error');
    } finally {
      setLoading(false);
    }
  }

  const noMatch = result !== null && result.matchedRouteId === null;

  return (
    <section aria-labelledby="route-match-panel-title">
      <h2
        id="route-match-panel-title"
        style={{
          fontSize: 'var(--font-size-lg)',
          fontWeight: 700,
          color: 'var(--color-text-primary)',
          marginBottom: 'var(--spacing-2)',
          marginTop: 0,
        }}
      >
        {t('showcase.routeMatch.title')}
      </h2>
      <p
        style={{
          color: 'var(--color-text-secondary)',
          fontSize: 'var(--font-size-sm)',
          marginBottom: 'var(--spacing-4)',
        }}
      >
        G001 — eraf-gateway
      </p>

      {/* Form */}
      <form onSubmit={handleSubmit} noValidate>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr auto',
            gap: 'var(--spacing-2)',
            marginBottom: 'var(--spacing-4)',
            alignItems: 'flex-end',
          }}
        >
          {/* Method select */}
          <div>
            <label
              htmlFor="route-method"
              style={{
                display: 'block',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 600,
                color: 'var(--color-text-secondary)',
                marginBottom: 'var(--spacing-1)',
              }}
            >
              {t('showcase.routeMatch.methodLabel')}
            </label>
            <select
              id="route-method"
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              aria-label={t('showcase.routeMatch.methodLabel')}
              style={{
                fontFamily: 'var(--font-family-mono)',
                fontSize: 'var(--font-size-sm)',
                padding: 'var(--spacing-2) var(--spacing-3)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-sm)',
                background: 'var(--color-surface-elevated)',
                color: 'var(--color-text-primary)',
                cursor: 'pointer',
              }}
            >
              {HTTP_METHODS.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>

          {/* Path input */}
          <div>
            <label
              htmlFor="route-path"
              style={{
                display: 'block',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 600,
                color: 'var(--color-text-secondary)',
                marginBottom: 'var(--spacing-1)',
              }}
            >
              {t('showcase.routeMatch.pathLabel')}
            </label>
            <input
              id="route-path"
              type="text"
              value={path}
              onChange={(e) => setPath(e.target.value)}
              placeholder="/api/eraf/users"
              aria-label={t('showcase.routeMatch.pathLabel')}
              style={{
                width: '100%',
                fontFamily: 'var(--font-family-mono)',
                fontSize: 'var(--font-size-sm)',
                padding: 'var(--spacing-2) var(--spacing-3)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-sm)',
                background: 'var(--color-surface-elevated)',
                color: 'var(--color-text-primary)',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Submit button */}
          <button
            type="submit"
            disabled={loading || !path.trim()}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: loading ? 'var(--color-surface-muted)' : 'var(--color-brand-primary)',
              color: loading ? 'var(--color-text-muted)' : 'var(--color-text-on-brand)',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              fontWeight: 700,
              fontSize: 'var(--font-size-sm)',
              cursor: loading ? 'not-allowed' : 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {loading ? '...' : t('showcase.routeMatch.matchButton')}
          </button>
        </div>
      </form>

      {/* Error state */}
      {error && (
        <div
          role="alert"
          style={{
            padding: 'var(--spacing-3)',
            background: 'var(--color-status-error-bg)',
            border: '1px solid var(--color-status-error)',
            borderRadius: 'var(--radius-sm)',
            color: 'var(--color-status-error)',
            fontSize: 'var(--font-size-sm)',
            marginBottom: 'var(--spacing-4)',
          }}
        >
          {error}
        </div>
      )}

      {/* Empty state — no match */}
      {noMatch && (
        <div
          role="status"
          style={{
            padding: 'var(--spacing-6)',
            textAlign: 'center',
            border: '1px dashed var(--color-surface-border)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--color-text-muted)',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          {t('showcase.routeMatch.noMatch')}
        </div>
      )}

      {/* Result card */}
      {result !== null && result.matchedRouteId !== null && (
        <div>
          <p
            style={{
              fontSize: 'var(--font-size-sm)',
              fontWeight: 700,
              color: 'var(--color-text-secondary)',
              marginBottom: 'var(--spacing-2)',
            }}
          >
            {t('showcase.routeMatch.matchedRouteLabel')}
          </p>
          <div
            style={{
              padding: 'var(--spacing-3) var(--spacing-4)',
              border: '1px solid var(--color-status-success)',
              borderRadius: 'var(--radius-md)',
              background: 'var(--color-status-success-bg)',
              marginBottom: 'var(--spacing-4)',
            }}
          >
            <dl
              style={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr',
                gap: 'var(--spacing-1) var(--spacing-4)',
                fontSize: 'var(--font-size-sm)',
                margin: 0,
              }}
            >
              {[
                ['Route ID', result.matchedRouteId],
                ['Name', result.name ?? '—'],
                ['Service', result.service ?? '—'],
                ['Priority', result.priority?.toString() ?? '—'],
              ].map(([label, value]) => (
                <div key={label} style={{ display: 'contents' }}>
                  <dt style={{ color: 'var(--color-text-muted)', fontWeight: 600 }}>{label}</dt>
                  <dd style={{ margin: 0, fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-primary)' }}>
                    {value}
                  </dd>
                </div>
              ))}
            </dl>
          </div>

          {/* Alternatives table */}
          {result.alternatives.length > 0 && (
            <div>
              <p
                style={{
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 700,
                  color: 'var(--color-text-secondary)',
                  marginBottom: 'var(--spacing-2)',
                }}
              >
                {t('showcase.routeMatch.alternativesLabel')}
              </p>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: 'var(--font-size-xs)',
                  fontFamily: 'var(--font-family-mono)',
                }}
                aria-label={t('showcase.routeMatch.alternativesLabel')}
              >
                <thead>
                  <tr>
                    {['Route ID', 'Name', 'Service', 'Priority'].map((h) => (
                      <th
                        key={h}
                        style={{
                          textAlign: 'left',
                          padding: 'var(--spacing-1) var(--spacing-2)',
                          borderBottom: '1px solid var(--color-surface-border)',
                          color: 'var(--color-text-muted)',
                          fontWeight: 600,
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.alternatives.slice(0, 3).map((alt) => (
                    <tr key={alt.routeId}>
                      <td style={{ padding: 'var(--spacing-1) var(--spacing-2)', color: 'var(--color-brand-primary)' }}>{alt.routeId}</td>
                      <td style={{ padding: 'var(--spacing-1) var(--spacing-2)', color: 'var(--color-text-secondary)' }}>{alt.name}</td>
                      <td style={{ padding: 'var(--spacing-1) var(--spacing-2)', color: 'var(--color-text-secondary)' }}>{alt.service}</td>
                      <td style={{ padding: 'var(--spacing-1) var(--spacing-2)', color: 'var(--color-text-muted)' }}>{alt.priority}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
